import { useEffect } from 'react';
import { useTheme } from 'src/hooks/useTheme';
import { cssVarMap, darkCssVarMap } from './tokens';

/**
 * ThemeProvider
 *
 * Injects all Ahedge brand CSS custom properties onto :root on mount and
 * whenever the active theme changes. CSS variables are also declared statically
 * in src/index.css for Tailwind JIT and initial paint — this component provides
 * the runtime guarantee and enables dynamic theming.
 *
 * Usage:
 *   <ThemeProvider>
 *     <App />
 *   </ThemeProvider>
 *
 * Optional overrides prop accepts { 'ah-variable-name': '#hexvalue' } to
 * override individual tokens at runtime without touching the token file.
 */
export function ThemeProvider({ children, overrides = null }) {
  const { theme } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    const activeMap = theme === 'dark' ? darkCssVarMap : cssVarMap;

    Object.entries(activeMap).forEach(([name, value]) => {
      root.style.setProperty(`--${name}`, value);
    });

    if (overrides && typeof overrides === 'object') {
      Object.entries(overrides).forEach(([name, value]) => {
        root.style.setProperty(`--${name}`, value);
      });
    }

    return () => {
      Object.keys(activeMap).forEach((name) => {
        root.style.removeProperty(`--${name}`);
      });
      if (overrides) {
        Object.keys(overrides).forEach((name) => {
          root.style.removeProperty(`--${name}`);
        });
      }
    };
  }, [theme, overrides]);

  return children;
}

export default ThemeProvider;
